const normalizeBase = (base) => {
  const cleaned = String(base || "").trim().replace(/\/+$/, "");
  return cleaned.toLowerCase().endsWith("/api") ? cleaned.slice(0, -4) : cleaned;
};

const API_BASE =
  normalizeBase(process.env.REACT_APP_BACKEND_URL) ||
  normalizeBase(process.env.REACT_APP_BACKEND) ||
  normalizeBase(process.env.REACT_APP_API_URL) ||
  "https://cphere-backend.onrender.com";

const SECRET_KEY_PATTERN =
  /(password|token|secret|authorization|cookie|oauth|api[_-]?key|connector[_-]?payload|request[_-]?body|full[_-]?prompt|prompt|raw[_-]?body|raw[_-]?request|payload)/i;

const METADATA_ALLOWLIST = {
  page_viewed: new Set(["path", "section", "tab"]),
  section_viewed: new Set(["path", "section", "tab"]),
  advertiser_switched: new Set(["fromAdvertiserIdHash", "toAdvertiserIdHash"]),
  campaign_viewed: new Set(["platform", "campaignIdHash", "campaignNameTruncated"]),
  report_viewed: new Set(["reportType", "dateRangePreset"]),
  eden_actions_viewed: new Set(["section", "tab"]),
  queue_console_viewed: new Set(["filter", "lane"]),
  approval_opened: new Set(["approvalType", "riskLevel"]),
  approval_approved: new Set(["approvalType", "riskLevel"]),
  approval_rejected: new Set(["approvalType", "riskLevel"]),
  dry_run_requested: new Set(["platform", "actionType"]),
  execute_attempted: new Set(["platform", "actionType", "riskLevel"]),
  integration_viewed: new Set(["platform"]),
  integration_connected: new Set(["platform"]),
  integration_disconnected: new Set(["platform"]),
  export_download_clicked: new Set(["exportType", "source"]),
  report_generated: new Set(["reportType", "source"]),
  system_logs_viewed: new Set(["tab", "filter"]),
};

const ENTITY_TYPES = new Set([
  "advertiser",
  "campaign",
  "report",
  "approval",
  "envelope",
  "deployment_plan",
  "integration",
  "user",
  "system_log",
]);

const ACTION_CLICK_RULES = [
  { pattern: /\bapprove\b/i, actionType: "approval_approved", entityType: "approval" },
  { pattern: /\breject\b/i, actionType: "approval_rejected", entityType: "approval" },
  { pattern: /\bdry\s*run\b/i, actionType: "dry_run_requested", entityType: "deployment_plan" },
  { pattern: /\bexecute\b/i, actionType: "execute_attempted", entityType: "deployment_plan" },
  {
    pattern: /\bdownload\b|\bexport\b/i,
    actionType: "export_download_clicked",
    entityType: "report",
    metadata: { source: "global_click_tracking" },
  },
  {
    pattern: /\bgenerate\b.*\breport\b|\breport\b.*\bgenerate\b/i,
    actionType: "report_generated",
    entityType: "report",
    metadata: { source: "global_click_tracking" },
  },
  { pattern: /\bdisconnect\b/i, actionType: "integration_disconnected", entityType: "integration" },
  { pattern: /\bconnect\b/i, actionType: "integration_connected", entityType: "integration" },
  { pattern: /\bopen\b.*\bapproval\b|\bapproval\b.*\bopen\b/i, actionType: "approval_opened", entityType: "approval" },
];

let lastEventKey = "";
let lastEventAt = 0;

const getStoredToken = () => {
  try {
    return (
      localStorage.getItem("clientAuthToken") ||
      localStorage.getItem("superadminToken") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      ""
    );
  } catch {
    return "";
  }
};

const trimValue = (value, max = 96) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
};

const normalizeActionType = (value) => {
  const normalized = trimValue(value, 96)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const aliases = {
    page_view: "page_viewed",
    route_viewed: "page_viewed",
    section_view: "section_viewed",
    advertiser_switch: "advertiser_switched",
    download_clicked: "export_download_clicked",
    export_clicked: "export_download_clicked",
    approve_clicked: "approval_approved",
    reject_clicked: "approval_rejected",
    dryrun_requested: "dry_run_requested",
    dry_run_clicked: "dry_run_requested",
    execute_clicked: "execute_attempted",
    system_logs_view: "system_logs_viewed",
  };
  const canonical = aliases[normalized] || normalized;
  return METADATA_ALLOWLIST[canonical] ? canonical : null;
};

const normalizeEntityType = (value) => {
  const normalized = trimValue(value, 80)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const aliases = {
    action: "deployment_plan",
    actions: "deployment_plan",
    deployment: "deployment_plan",
    deploymentplan: "deployment_plan",
    log: "system_log",
    system_logs: "system_log",
  };
  const canonical = aliases[normalized] || normalized;
  return ENTITY_TYPES.has(canonical) ? canonical : null;
};

export const normalizeRoutePath = (value = "") => {
  const raw = trimValue(value, 500);
  if (!raw) return "unknown";

  try {
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      const currentHost = typeof window !== "undefined" ? window.location.hostname : "";
      if (currentHost && parsed.hostname !== currentHost) return "unknown";
      return normalizeRoutePath(parsed.pathname || "/");
    }

    const withoutQuery = raw.split("#")[0].split("?")[0].trim();
    if (!withoutQuery) return "unknown";
    const path = withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
    return path.replace(/\/{2,}/g, "/").replace(/\/+$/, "") || "/";
  } catch {
    return "unknown";
  }
};

const normalizeAdvertiserId = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const sanitizeMetadata = (actionType, metadata = {}) => {
  const allowed = METADATA_ALLOWLIST[actionType];
  if (!allowed || !metadata || typeof metadata !== "object" || Array.isArray(metadata)) return {};

  const out = {};
  allowed.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(metadata, key)) return;
    if (SECRET_KEY_PATTERN.test(key)) return;
    const raw = metadata[key];
    if (raw === null || raw === undefined || typeof raw === "object") return;
    const value = trimValue(raw, key === "campaignNameTruncated" ? 80 : 96);
    if (!value) return;
    if (key === "path") {
      out[key] = normalizeRoutePath(value);
      return;
    }
    if (SECRET_KEY_PATTERN.test(value)) return;
    if (/Hash$/.test(key) && !/^[a-z]+_[a-f0-9]{12,32}$/i.test(value)) return;
    out[key] = value.replace(/[\r\n\t]+/g, " ");
  });
  return out;
};

export function sanitizeDashboardActivityEvent(event = {}) {
  const actionType = normalizeActionType(event.actionType);
  if (!actionType) return null;

  const fallbackPath = typeof window !== "undefined" ? window.location.pathname : "";
  const routeSection = normalizeRoutePath(event.routeSection || fallbackPath);
  const entityType = normalizeEntityType(event.entityType);
  const metadata = sanitizeMetadata(actionType, {
    path: routeSection,
    ...(event.metadata || {}),
  });

  return {
    actionType,
    routeSection,
    advertiserId: normalizeAdvertiserId(event.advertiserId),
    entityType,
    metadata,
  };
}

export function classifyRouteActivity(pathname = "") {
  const routeSection = normalizeRoutePath(pathname);
  if (routeSection === "/system-logs") return { actionType: "system_logs_viewed", routeSection };
  if (routeSection === "/eden-actions") return { actionType: "eden_actions_viewed", routeSection };
  if (routeSection === "/eden-actions-queue") return { actionType: "queue_console_viewed", routeSection };
  if (/integration/i.test(routeSection)) return { actionType: "integration_viewed", routeSection };
  if (/report/i.test(routeSection)) return { actionType: "report_viewed", routeSection };
  if (/campaign|optimus|analytics|dashboard/i.test(routeSection)) {
    return { actionType: "page_viewed", routeSection };
  }
  return { actionType: "section_viewed", routeSection };
}

export function resetDashboardActivityTrackerForTests() {
  lastEventKey = "";
  lastEventAt = 0;
}

export function trackDashboardActivity(event = {}) {
  try {
    const token = getStoredToken();
    const body = sanitizeDashboardActivityEvent(event);
    if (!token || !body) return Promise.resolve(false);

    const eventKey = JSON.stringify(body);
    const now = Date.now();
    const viewEvent = /_viewed$/.test(body.actionType) || body.actionType === "section_viewed";
    const dedupeMs = viewEvent ? 10000 : 1500;
    if (eventKey === lastEventKey && now - lastEventAt < dedupeMs) {
      return Promise.resolve(false);
    }
    lastEventKey = eventKey;
    lastEventAt = now;

    return fetch(`${API_BASE}/api/system/logs/dashboard-activity`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      cache: "no-store",
      keepalive: true,
      body: JSON.stringify(body),
    })
      .then((res) => res.ok)
      .catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
}

export function installDashboardActivityClickTracking({ getContext } = {}) {
  if (typeof document === "undefined") return () => {};

  const handler = (event) => {
    const target = event.target?.closest?.("button,a,[role='button']");
    if (!target) return;

    const explicitAction = target.getAttribute("data-system-log-action");
    const explicitEntityType = target.getAttribute("data-system-log-entity-type");
    const label = [
      target.getAttribute("aria-label"),
      target.getAttribute("title"),
      target.textContent,
    ]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();

    const rule = explicitAction
      ? { actionType: explicitAction, entityType: explicitEntityType || null }
      : ACTION_CLICK_RULES.find((candidate) => candidate.pattern.test(label));
    if (!rule) return;

    const context = typeof getContext === "function" ? getContext() || {} : {};
    trackDashboardActivity({
      ...context,
      actionType: rule.actionType,
      entityType: context.entityType || rule.entityType,
      metadata: rule.metadata || {},
    });
  };

  document.addEventListener("click", handler, true);
  return () => document.removeEventListener("click", handler, true);
}
